import { useCallback, useEffect, useRef, useState } from 'react';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import ProgressBar from '@/components/base/ProgressBar';
import {
  MIN_SEEDS_PER_MEASURE,
  runRhythmRuleChecks,
  runRhythmVerificationAsync,
  type RuleCase,
  type RuleReport,
  type SignatureReport,
  type VerificationProgress,
  type VerificationReport,
} from '@/lib/exercise/verification';

const NUMBER_FORMAT = new Intl.NumberFormat('pt-BR');

function formatNumber(value: number): string {
  return NUMBER_FORMAT.format(value);
}

interface StatusBadgeProps {
  invalid: number;
}

function StatusBadge({ invalid }: StatusBadgeProps) {
  if (invalid === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-label font-semibold text-accent-800">
        <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" />
        Válido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-label font-semibold text-secondary-800">
      <i className="ri-close-line w-3.5 h-3.5 flex items-center justify-center" />
      {formatNumber(invalid)} inválido{invalid > 1 ? 's' : ''}
    </span>
  );
}

function FailureList({ title, failures }: { title: string; failures: string[] }) {
  if (failures.length === 0) return null;
  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50/60 p-3">
      <div className="flex items-center gap-2">
        <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center text-secondary-700" />
        <span className="font-label text-xs font-bold text-secondary-800">
          {title} ({failures.length})
        </span>
      </div>
      <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">
        {failures.map((failure) => (
          <li
            key={failure}
            className="rounded-md bg-background-50 px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-foreground-700"
          >
            {failure}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DiagnosticPage() {
  const [seeds, setSeeds] = useState(MIN_SEEDS_PER_MEASURE);
  const [report, setReport] = useState<VerificationReport | null>(null);
  const [ruleReport, setRuleReport] = useState<RuleReport | null>(null);
  const [progress, setProgress] = useState<VerificationProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [ranAt, setRanAt] = useState<string | null>(null);
  const busyRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const handleRun = useCallback((seedCount: number) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setRunning(true);
    // Não mantemos nenhum resultado anterior visível: a verificação NÃO pode
    // aparecer como aprovada antes de todas as sementes terminarem.
    setReport(null);
    setRuleReport(null);
    setProgress({ stage: 'Iniciando…', completed: 0, total: 3 });
    const startedAt = new Date();
    runRhythmVerificationAsync(seedCount, (next) => {
      if (aliveRef.current) setProgress(next);
    })
      .then((result) => {
        if (!aliveRef.current) return;
        setReport(result);
        setRuleReport(runRhythmRuleChecks());
        setProgress(null);
        setRanAt(startedAt.toLocaleTimeString('pt-BR'));
      })
      .finally(() => {
        busyRef.current = false;
        if (aliveRef.current) setRunning(false);
      });
  }, []);

  useEffect(() => {
    handleRun(seeds);
    // roda uma vez ao abrir; ajustes manuais usam o botão
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalInvalid = report?.invalidTotal ?? 0;
  const progressPercent = progress ? (progress.completed / progress.total) * 100 : 0;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="mx-auto max-w-6xl">
            <Badge tone="secondary" icon="ri-tools-line">
              Ferramenta interna de diagnóstico
            </Badge>
            <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
              Verificação do gerador de compassos
            </h1>
            <p className="mt-4 max-w-3xl text-foreground-700">
              Esta página roda a verificação automática <strong>aqui mesmo no navegador</strong> e
              mostra os casos inválidos dos compassos 2/4, 3/4 e 4/4. Para cada fórmula, o gerador
              cria milhares de exercícios e confere se a soma das durações de cada compasso bate
              exatamente com a fórmula — sem notas sobrepostas nem espaços vazios.
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-10 md:px-8 md:py-14">
          <div className="mx-auto max-w-6xl space-y-6">
            {/* Controles */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-[240px] flex-1">
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="seeds" className="font-label text-sm font-semibold text-foreground-700">
                      Sementes por combinação
                    </label>
                    <span className="font-heading text-base font-bold text-primary-700">
                      {formatNumber(seeds)}
                    </span>
                  </div>
                  <input
                    id="seeds"
                    type="range"
                    min={MIN_SEEDS_PER_MEASURE}
                    max={240}
                    step={10}
                    value={seeds}
                    disabled={running}
                    onChange={(event) => setSeeds(Number(event.target.value))}
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-background-200 accent-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <p className="mt-1 text-xs text-foreground-500">
                    Mínimo de {formatNumber(MIN_SEEDS_PER_MEASURE)} por combinação — 120 para 2/4,
                    120 para 3/4 e 120 para 4/4. Cada fórmula roda esse valor multiplicado por{' '}
                    {formatNumber(120)} combinações (barras × figuras × notas). Mais sementes =
                    mais exercícios conferidos.
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  icon={running ? 'ri-loader-4-line' : 'ri-play-line'}
                  disabled={running}
                  onClick={() => handleRun(seeds)}
                >
                  {running ? 'Verificando…' : 'Rodar verificação'}
                </Button>
              </div>

              {running && progress ? (
                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-xs text-foreground-600">
                    <span className="font-label font-semibold">
                      Executando: {progress.stage}
                    </span>
                    <span>
                      {progress.completed}/{progress.total} etapas
                    </span>
                  </div>
                  <ProgressBar value={progressPercent} max={100} tone="primary" />
                  <p className="mt-1.5 text-xs text-foreground-500">
                    O resultado só é apresentado quando todas as sementes terminarem.
                  </p>
                </div>
              ) : null}

              {!running && ranAt ? (
                <p className="mt-3 text-xs text-foreground-500">
                  Última execução concluída às {ranAt}, com {formatNumber(seeds)} sementes por
                  combinação. Resultado calculado no seu navegador.
                </p>
              ) : null}
            </div>

            {/* Resumo geral */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-label text-sm font-bold text-foreground-800">
                  Resumo geral
                </span>
                {running ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-label font-semibold text-primary-800">
                    <i className="ri-loader-4-line w-3.5 h-3.5 flex items-center justify-center" />
                    Verificando… (ainda não avaliada)
                  </span>
                ) : report ? (
                  report.ok ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-label font-semibold text-accent-800">
                      <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center" />
                      Nenhum compasso inválido
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-label font-semibold text-secondary-800">
                      <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center" />
                      {formatNumber(totalInvalid)} caso(s) inválido(s)
                    </span>
                  )
                ) : (
                  <span className="text-xs font-label font-semibold text-foreground-500">
                    Aguardando
                  </span>
                )}
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-background-100 p-3">
                  <div className="font-heading text-2xl font-bold text-foreground-950">
                    {report ? formatNumber(report.measureSums.generated) : '—'}
                  </div>
                  <div className="text-xs text-foreground-500">Compassos gerados e conferidos</div>
                </div>
                <div className="rounded-lg bg-background-100 p-3">
                  <div
                    className={`font-heading text-2xl font-bold ${
                      report && totalInvalid === 0 ? 'text-accent-700' : 'text-secondary-700'
                    }`}
                  >
                    {report ? formatNumber(totalInvalid) : '—'}
                  </div>
                  <div className="text-xs text-foreground-500">Casos inválidos</div>
                </div>
                <div className="rounded-lg bg-background-100 p-3">
                  <div className="font-heading text-2xl font-bold text-foreground-950">
                    {report ? formatNumber(report.measureSums.seedsPerCombination) : '—'}
                  </div>
                  <div className="text-xs text-foreground-500">Sementes por combinação</div>
                </div>
              </div>
            </div>

            {/* Por fórmula */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <i className="ri-ruler-2-line w-5 h-5 flex items-center justify-center text-primary-600" />
                <span className="font-label text-sm font-bold text-foreground-800">
                  Casos inválidos por fórmula
                </span>
              </div>

              {report ? (
                <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full min-w-[680px] border-collapse text-left text-sm">
                    <thead>
                      <tr className="bg-background-100 text-[11px] font-label font-bold text-foreground-600">
                        <th className="px-4 py-2.5">Fórmula</th>
                        <th className="px-4 py-2.5">Sementes/combinação</th>
                        <th className="px-4 py-2.5">Combinações</th>
                        <th className="px-4 py-2.5">Casos executados</th>
                        <th className="px-4 py-2.5">Inválidos</th>
                        <th className="px-4 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.measureSums.bySignature.map((signature: SignatureReport) => (
                        <tr key={signature.beatsPerMeasure} className="border-t border-background-200">
                          <td className="px-4 py-2.5 font-heading font-bold text-foreground-950">
                            {signature.beatsPerMeasure}/4
                          </td>
                          <td className="px-4 py-2.5 text-foreground-700">
                            {formatNumber(signature.seedsPerCombination)}
                          </td>
                          <td className="px-4 py-2.5 text-foreground-700">
                            {formatNumber(signature.combinations)}
                          </td>
                          <td className="px-4 py-2.5 text-foreground-700">
                            {formatNumber(signature.generated)}
                          </td>
                          <td className="px-4 py-2.5 font-heading font-bold text-foreground-950">
                            {formatNumber(signature.invalid)}
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge invalid={signature.invalid} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-3 text-sm text-foreground-500">
                  {running ? 'Verificando… o resultado aparece só no fim.' : 'Aguardando a verificação…'}
                </p>
              )}
            </div>

            {/* Cenários */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <i className="ri-focus-3-line w-5 h-5 flex items-center justify-center text-primary-600" />
                <span className="font-label text-sm font-bold text-foreground-800">
                  Cenários especiais (última pausa e última ligadura)
                </span>
              </div>
              {report ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    {
                      label: 'Termina em pausa',
                      icon: 'ri-pause-circle-line',
                      data: report.lastRest,
                    },
                    {
                      label: 'Termina em ligadura',
                      icon: 'ri-link',
                      data: report.lastTie,
                    },
                  ].map((scenario) => (
                    <div key={scenario.label} className="rounded-lg border border-background-200 bg-background-100/60 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 font-label text-xs font-bold text-foreground-800">
                          <i className={`${scenario.icon} w-4 h-4 flex items-center justify-center text-primary-600`} />
                          {scenario.label}
                        </span>
                        <StatusBadge invalid={scenario.data.invalid} />
                      </div>
                      <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                        <div>
                          <div className="font-heading text-sm font-bold text-foreground-950">
                            {formatNumber(scenario.data.generated)}
                          </div>
                          <div className="text-[10px] text-foreground-500">gerados</div>
                        </div>
                        <div>
                          <div className="font-heading text-sm font-bold text-foreground-950">
                            {formatNumber(scenario.data.matched)}
                          </div>
                          <div className="text-[10px] text-foreground-500">no cenário</div>
                        </div>
                        <div>
                          <div className="font-heading text-sm font-bold text-foreground-950">
                            {formatNumber(scenario.data.seedsPerCombination)}
                          </div>
                          <div className="text-[10px] text-foreground-500">sementes</div>
                        </div>
                        <div>
                          <div className="font-heading text-sm font-bold text-foreground-950">
                            {formatNumber(scenario.data.invalid)}
                          </div>
                          <div className="text-[10px] text-foreground-500">inválidos</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-sm text-foreground-500">
                  {running ? 'Verificando… o resultado aparece só no fim.' : 'Aguardando a verificação…'}
                </p>
              )}
            </div>

            {/* Regra de desbloqueio e de duração */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <i className="ri-key-2-line w-5 h-5 flex items-center justify-center text-primary-600" />
                  <span className="font-label text-sm font-bold text-foreground-800">
                    Regra de desbloqueio e de duração (casos reproduzíveis)
                  </span>
                </div>
                {running ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-label font-semibold text-primary-800">
                    <i className="ri-loader-4-line w-3.5 h-3.5 flex items-center justify-center" />
                    Verificando… (ainda não avaliada)
                  </span>
                ) : ruleReport ? (
                  ruleReport.ok ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-label font-semibold text-accent-800">
                      <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center" />
                      {formatNumber(ruleReport.cases.length)} caso(s), 0 falha
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-label font-semibold text-secondary-800">
                      <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center" />
                      {formatNumber(ruleReport.failed)} falha(s)
                    </span>
                  )
                ) : (
                  <span className="text-xs font-label font-semibold text-foreground-500">Aguardando</span>
                )}
              </div>

              <p className="mt-3 text-xs text-foreground-600">
                Casos determinísticos que provam a <strong>regra de desbloqueio</strong> (execução sem
                erros por item) e a <strong>regra de duração</strong> (segurar até perto do fim). O
                mesmo módulo roda no navegador e fora dele.
              </p>

              {ruleReport ? (
                <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                    <thead>
                      <tr className="bg-background-100 text-[11px] font-label font-bold text-foreground-600">
                        <th className="px-3 py-2">Caso</th>
                        <th className="px-3 py-2">Esperado</th>
                        <th className="px-3 py-2">Obtido</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleReport.cases.map((ruleCase: RuleCase) => (
                        <tr key={ruleCase.id} className="border-t border-background-200">
                          <td className="px-3 py-2 text-foreground-800">{ruleCase.title}</td>
                          <td className="px-3 py-2 font-mono text-[11px] text-foreground-600">
                            {ruleCase.expected}
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px] text-foreground-600">
                            {ruleCase.actual}
                          </td>
                          <td className="px-3 py-2">
                            {ruleCase.passed ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-label font-semibold text-accent-800">
                                <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" /> ok
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-label font-semibold text-secondary-800">
                                <i className="ri-close-line w-3.5 h-3.5 flex items-center justify-center" /> falhou
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-3 text-sm text-foreground-500">
                  {running ? 'Verificando… o resultado aparece só no fim.' : 'Aguardando a verificação…'}
                </p>
              )}
            </div>

            {/* Lista de falhas */}
            {report && totalInvalid > 0 ? (
              <div className="space-y-3 rounded-lg border border-secondary-200 bg-background-50 p-5">
                <div className="flex items-center gap-2">
                  <i className="ri-bug-line w-5 h-5 flex items-center justify-center text-secondary-700" />
                  <span className="font-label text-sm font-bold text-foreground-800">
                    Detalhe dos casos inválidos
                  </span>
                </div>
                {report.measureSums.bySignature.map((signature) => (
                  <FailureList
                    key={signature.beatsPerMeasure}
                    title={`Compasso ${signature.beatsPerMeasure}/4`}
                    failures={signature.failures}
                  />
                ))}
                <FailureList title="Cenário: termina em pausa" failures={report.lastRest.failures} />
                <FailureList title="Cenário: termina em ligadura" failures={report.lastTie.failures} />
                <p className="text-xs text-foreground-500">
                  Cada lista mostra até 200 exemplos. Se houver muitos casos, todos compartilham a
                  mesma causa — o total de inválidos acima continua sendo o real.
                </p>
              </div>
            ) : null}

            <p className="text-xs text-foreground-500">
              Observação: esta ferramenta confere a <strong>soma das durações</strong> de cada
              compasso e a coerência figura↔duração. Ela não substitui o teste de som, toque e
              histórico no aparelho — isso depende de ouvir e tocar de verdade.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}